import React from 'react';

import { Box, Col, Row } from '@opendoor/atoms/box';

import { TRANSACTION_CARD_CLASSES } from './TransactionCardLayout';

interface SkeletonBlockProps {
  className?: string;
}

/**
 * Neutral placeholder block with a sweeping shimmer. Decorative only, so it is
 * hidden from the accessibility tree; screen readers announce the real content
 * once it loads.
 */
const SkeletonBlock: React.FC<SkeletonBlockProps> = ({ className }) => (
  <Box aria-hidden className={`skeleton rounded-md bg-od-bg-tertiary ${className ?? ''}`} />
);

// Stable keys so the placeholder rows don't lean on array indices.
const SAVED_HOME_SKELETON_KEYS = ['a', 'b', 'c', 'd'];
const TOUR_SKELETON_KEYS = ['a', 'b'];

/** Loading placeholder for the Saved homes carousel; mirrors `SavedHomeCard`. */
export const SavedHomesSkeleton: React.FC = () => (
  <Row aria-hidden className="gap-4 overflow-hidden">
    {SAVED_HOME_SKELETON_KEYS.map((key) => (
      <Col key={key} className="w-[min(74vw,260px)] shrink-0">
        <SkeletonBlock className="h-[145px] w-full rounded-xl" />
        <Col className="gap-2 pt-3 pb-4">
          <SkeletonBlock className="h-4 w-3/4" />
          <SkeletonBlock className="h-3 w-1/2" />
          <SkeletonBlock className="h-3 w-2/3" />
        </Col>
      </Col>
    ))}
  </Row>
);

/** Loading placeholder for the Home tours list; mirrors `TourCard` rows. */
export const HomeToursSkeleton: React.FC = () => (
  <Col aria-hidden className="gap-4">
    {TOUR_SKELETON_KEYS.map((key) => (
      <Col key={key} className="w-full md:flex-row md:items-center md:gap-4">
        <SkeletonBlock className="h-[161px] w-full shrink-0 rounded-xl md:h-[131px] md:w-[197px]" />
        <Col className="min-w-0 flex-1 gap-2 py-3 md:py-2">
          <SkeletonBlock className="h-4 w-1/2" />
          <SkeletonBlock className="h-3 w-1/3" />
          <SkeletonBlock className="h-3 w-2/5" />
        </Col>
      </Col>
    ))}
  </Col>
);

/**
 * Loading placeholder mirroring `TransactionCard`. Shared by the Home sale and
 * Home purchase sections, which each render a single full-width transaction card
 * (its active offer) by default.
 *
 * Block heights mirror the real card's typography line-heights so the skeleton
 * reserves the same vertical space and the layout doesn't shift once content
 * loads: address is `text-label-md` (20px), price/details/status are
 * `text-body-sm` (~20px) with the card's `gap-1`, the progress bar is 14px, the
 * footer message is `text-body-sm` on mobile / `text-body-md` (24px) on desktop,
 * and the CTA is the `md` button (`h-11`). Shared `TRANSACTION_CARD_CLASSES`
 * keeps the real card and skeleton aligned as the card layout evolves.
 */
export const TransactionCardSkeleton: React.FC = () => (
  <Col aria-hidden className={TRANSACTION_CARD_CLASSES.container}>
    <Box className={TRANSACTION_CARD_CLASSES.innerCard}>
      {/* Mirror the real card's media slot: ≈161px tall and full-width on
          mobile (image stacked above the body), ≈131px×197px on desktop. */}
      <Box className={`${TRANSACTION_CARD_CLASSES.mediaSlot} skeleton bg-od-bg-tertiary`} />
      <Col className={TRANSACTION_CARD_CLASSES.body}>
        <Col className={TRANSACTION_CARD_CLASSES.details}>
          <SkeletonBlock className="h-5 w-1/2" />
          <SkeletonBlock className="h-5 w-1/3" />
          <SkeletonBlock className="h-5 w-2/5" />
        </Col>
        <Box className={TRANSACTION_CARD_CLASSES.divider} />
        <Row className={TRANSACTION_CARD_CLASSES.status}>
          <SkeletonBlock className="h-3.5 w-[64px]" />
          <SkeletonBlock className="h-5 w-[88px]" />
        </Row>
      </Col>
    </Box>
    <Row className={TRANSACTION_CARD_CLASSES.footer}>
      <SkeletonBlock className="h-6 w-2/5" />
      <Box className={TRANSACTION_CARD_CLASSES.ctaSkeleton} />
    </Row>
  </Col>
);
